import React from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import { Box, IconButton, useTheme } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AttachmentList from "components/basic/attachmentList";
import Typography from "components/basic/typography";

import { TicketComment } from "../../../../../../types";

interface CommentBubbleProps {
  comment: TicketComment;
  isMine: boolean;
  onDelete?: () => void;
}

const CommentBubble: React.FC<CommentBubbleProps> = ({
  comment,
  isMine,
  onDelete,
}) => {
  const theme = useTheme();
  const { t } = useTranslation("support");
  const format = useFormat();

  return (
    <Box
      display="flex"
      justifyContent={isMine ? "flex-end" : "flex-start"}
      px={2}
      py={0.5}
    >
      <Box maxWidth={{ xs: "85%", md: "70%" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent={isMine ? "flex-end" : "flex-start"}
          gap={0.5}
        >
          <Typography
            variant="caption"
            style={{ color: theme.palette.text.secondary, margin: "0 8px" }}
          >
            {comment.authorName}
          </Typography>
          {onDelete && (
            <IconButton
              size="small"
              onClick={onDelete}
              aria-label={t("detail.delete_comment")}
              title={t("detail.delete_comment")}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            backgroundColor: isMine
              ? theme.palette.primary.main
              : theme.palette.action.hover,
            color: isMine
              ? theme.palette.primary.contrastText
              : theme.palette.text.primary,
            borderRadius: 2,
            px: 1.5,
            py: 1,
          }}
        >
          <Typography
            variant="body1"
            style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
          >
            {comment.body || t("detail.sent_attachments")}
          </Typography>
        </Box>
        {comment.attachments.length > 0 && (
          <Box mt={0.5}>
            <AttachmentList attachments={comment.attachments} title="" />
          </Box>
        )}
        <Typography
          variant="caption"
          style={{
            color: theme.palette.text.secondary,
            display: "block",
            textAlign: isMine ? "right" : "left",
            margin: "2px 8px 0",
          }}
        >
          {format.timestamp(comment.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

export default CommentBubble;
